// src/pages/dashboard/DashboardAdmin.tsx - IMPROVED VERSION
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Building2, FileText, Database, Settings, Activity, Shield,
  Server, HardDrive, TrendingUp, CheckCircle, ArrowUpRight,
  RefreshCw, Lock, Workflow, BarChart3, AlertTriangle, Clock,
  Eye, Calendar, Zap, Package, Layers,
} from 'lucide-react';
import { Card, Badge, Breadcrumb, Loading, LogoLoadingMark } from '../../components/ui';
import api from '../../services/api';
import { useCapabilities } from '../../hooks/useCapabilities';
import { normalizeSystemUptime } from '../../utils/systemInfo';
import { normalizeSystemHealth, NormalizedSystemHealth } from '../../utils/systemHealth';

interface SystemStats {
  total_users: number;
  active_users: number;
  total_units: number;
  total_laporan: number;
  total_roles: number;
  total_capabilities: number;
  uptime: string;
  uptime_available: boolean;
  uptime_note: string;
  health_summary: string;
  avg_response: string;
  storage_used: number;
  storage_total: number;
  storage_used_label: string;
  db_size: string;
  app_version: string;
}

interface ActivityLog {
  id: number;
  log_name: string;
  description: string;
  created_at: string;
  causer?: { name: string };
}

interface RoleStats {
  role: string;
  display_name?: string;
  count: number;
  percentage: number;
}

const DEFAULT_STORAGE_TOTAL_GB = 10;

const parseStorageToGb = (rawValue: unknown): number => {
  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return rawValue;
  }

  if (typeof rawValue !== 'string') {
    return 0;
  }

  const normalized = rawValue.trim().toUpperCase();
  const match = normalized.match(/^([\d.,]+)\s*(KB|MB|GB|TB)?$/);
  if (!match) {
    return 0;
  }

  const value = Number(match[1].replace(',', '.'));
  if (!Number.isFinite(value)) {
    return 0;
  }

  const unit = match[2] || 'MB';

  if (unit === 'TB') return value * 1024;
  if (unit === 'GB') return value;
  if (unit === 'MB') return value / 1024;
  return value / 1024 / 1024;
};

const formatStorageLabel = (valueGb: number): string => {
  if (!Number.isFinite(valueGb) || valueGb <= 0) {
    return '0 MB';
  }

  if (valueGb >= 1) {
    return `${valueGb.toFixed(2)} GB`;
  }

  const valueMb = valueGb * 1024;
  if (valueMb >= 1) {
    return `${valueMb.toFixed(2)} MB`;
  }

  return `${(valueMb * 1024).toFixed(0)} KB`;
};

const defaultSystemHealth: NormalizedSystemHealth = {
  status: 'warning',
  summary: 'Status kesehatan sistem belum tersedia.',
  checks: [],
  issues: [],
  issueCount: 0,
  healthyCount: 0,
};

const DashboardAdmin: React.FC = () => {
  const caps = useCapabilities();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [systemStats, setSystemStats] = useState<SystemStats>({
    total_users: 0,
    active_users: 0,
    total_units: 0,
    total_laporan: 0,
    total_roles: 0,
    total_capabilities: 0,
    uptime: 'Tidak tersedia',
    uptime_available: false,
    uptime_note: 'Monitoring sistem aktif',
    health_summary: 'Status kesehatan sistem belum tersedia.',
    avg_response: 'N/A',
    storage_used: 0,
    storage_total: DEFAULT_STORAGE_TOTAL_GB,
    storage_used_label: '0 MB',
    db_size: '-',
    app_version: '1.0.0',
  });
  const [roleStats, setRoleStats] = useState<RoleStats[]>([]);
  const [recentLogs, setRecentLogs] = useState<ActivityLog[]>([]);
  const [systemHealth, setSystemHealth] = useState<NormalizedSystemHealth>(defaultSystemHealth);
  const [stuckCount, setStuckCount] = useState(0);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [
        dashRes,
        usersRes,
        activeUsersRes,
        rolesRes,
        unitsRes,
        logsRes,
        sysInfoRes,
      ] = await Promise.all([
        api.get('/dashboard').catch(() => ({ data: {} })),
        api.get('/admin/users', { params: { page: 1, per_page: 1 } }).catch(() => ({ data: { data: { total: 0 } } })),
        api.get('/admin/users', { params: { page: 1, per_page: 1, is_active: true } }).catch(() => ({ data: { data: { total: 0 } } })),
        api.get('/admin/roles').catch(() => ({ data: [] })),
        api.get('/admin/units/all').catch(() => ({ data: [] })),
        api.get('/admin/logs', { params: { per_page: 10 } }).catch(() => ({ data: { data: [] } })),
        api.get('/admin/system/info').catch((err) => {
          console.warn('[DashboardAdmin] system/info gagal:', err?.response?.status, err?.message);
          return { data: { data: null } };
        }),
      ]);

      // ✅ FIX: Parse dashboard data
      const dashData = dashRes.data?.data || dashRes.data || {};
      setStuckCount(Number(dashData.stuck_validations_count) || 0);

      // ✅ FIX: Parse user counts - handle nested response structure
      // API returns: { data: { current_page, data: [...], total, last_page } }
      // OR: { data: [...], meta: { total } }
      const parseUserTotal = (res: any): number => {
        const d = res?.data;
        if (!d) return 0;

        // Format: { data: { total: N, ... } }
        if (d.data?.total !== undefined) return Number(d.data.total);
        // Format: { data: { data: [...], total: N } }
        if (d.data?.data && d.data.total !== undefined) return Number(d.data.total);
        // Format: { total: N, ... }
        if (d.total !== undefined) return Number(d.total);
        // Format: { meta: { total: N } }
        if (d.meta?.total !== undefined) return Number(d.meta.total);

        // Fallback: array length
        if (Array.isArray(d.data)) return d.data.length;
        if (Array.isArray(d)) return d.length;

        return 0;
      };

      const totalUsersCount = parseUserTotal(usersRes);
      const activeUsersCount = parseUserTotal(activeUsersRes);

      // ✅ FIX: Parse roles - handle both array and wrapped response
      const rolesData = Array.isArray(rolesRes.data)
        ? rolesRes.data
        : Array.isArray(rolesRes.data?.data)
          ? rolesRes.data.data
          : [];

      // ✅ FIX: Parse units
      const unitsData = Array.isArray(unitsRes.data)
        ? unitsRes.data
        : Array.isArray(unitsRes.data?.data)
          ? unitsRes.data.data
          : [];

      // ✅ FIX: Parse logs
      const logsRaw = logsRes.data?.data || logsRes.data || [];
      const logsData = Array.isArray(logsRaw)
        ? logsRaw
        : Array.isArray(logsRaw?.data)
          ? logsRaw.data
          : [];

      // ✅ FIX: Parse system info
      const sysRaw = sysInfoRes.data?.data ?? sysInfoRes.data ?? null;
      const sysInfo = (sysRaw && typeof sysRaw === 'object') ? sysRaw : null;
      const systemInfoUnavailable = !sysInfo;
      const storageUsedGb = parseStorageToGb(sysInfo?.storage_used);
      const storageTotalGb = Number(sysInfo?.storage_total_gb);
      const normalizedStorageTotalGb = Number.isFinite(storageTotalGb) && storageTotalGb > 0
        ? storageTotalGb
        : DEFAULT_STORAGE_TOTAL_GB;
      const storageUsedLabel = formatStorageLabel(storageUsedGb);
      const uptimeInfo = normalizeSystemUptime(systemInfoUnavailable
        ? {
          uptime_available: false,
          uptime_note: 'Gagal memuat info uptime dari server.',
        }
        : sysInfo);
      const healthInfo = normalizeSystemHealth(systemInfoUnavailable
        ? {
          status: 'warning',
          summary: 'Gagal memuat data System Health dari server.',
          checks: [],
        }
        : sysInfo.health);
      const responseTimeMs = Number(sysInfo?.response_time_ms);
      const responseLabel = Number.isFinite(responseTimeMs) && responseTimeMs > 0
        ? `${responseTimeMs}ms`
        : (systemInfoUnavailable ? 'Error' : 'N/A');

      // ✅ FIX: Count unique capabilities from all roles (not hardcoded)
      const allCapabilities = new Set<string>();
      rolesData.forEach((r: any) => {
        if (Array.isArray(r.capabilities)) {
          r.capabilities.forEach((c: any) => {
            const capName = typeof c === 'string' ? c : c?.capability || c?.name;
            if (capName) allCapabilities.add(capName);
          });
        }
      });
      // Fallback: if no capabilities found in roles data, use known count
      const totalCaps = allCapabilities.size > 0 ? allCapabilities.size : 21;

      // Update system stats
      setSystemStats({
        total_users: totalUsersCount,
        active_users: activeUsersCount,
        total_units: unitsData.length || 0,
        total_laporan: dashData.total_laporan || 0,
        total_roles: rolesData.length || 0,
        total_capabilities: totalCaps,
        uptime: uptimeInfo.value,
        uptime_available: uptimeInfo.available,
        uptime_note: uptimeInfo.dashboardText,
        health_summary: healthInfo.summary,
        avg_response: responseLabel,
        storage_used: storageUsedGb,
        storage_total: normalizedStorageTotalGb,
        storage_used_label: storageUsedLabel,
        db_size: typeof sysInfo?.db_size === 'string' ? sysInfo.db_size : '-',
        app_version: typeof sysInfo?.app_version === 'string' ? sysInfo.app_version : '1.0.0',
      });
      setSystemHealth(healthInfo);

      // ✅ FIX: Role distribution - use users_count from roles API
      const roleDistribution: RoleStats[] = rolesData.map((r: any) => ({
        role: r.name,
        display_name: r.display_name,
        count: r.users_count || 0,
        percentage: 0,
      }));

      const totalInRoles = roleDistribution.reduce((sum, r) => sum + r.count, 0);
      roleDistribution.forEach(r => {
        r.percentage = totalInRoles > 0 ? (r.count / totalInRoles) * 100 : 0;
      });

      setRoleStats(roleDistribution.sort((a, b) => b.count - a.count).slice(0, 8));

      // Recent logs
      setRecentLogs(Array.isArray(logsData) ? logsData.slice(0, 8) : []);

    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setStuckCount(0);
      setSystemHealth(defaultSystemHealth);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return <Loading variant="page" message="Memuat dashboard administrator..." />;
  }

  // ✅ ADD: Access guard
  if (!loading && !caps.canManageUsers && !caps.canManageSettings) {
    return (
      <div className="space-y-6 animate-in">
        <Breadcrumb items={[{ label: 'Admin' }]} />
        <Card className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-warning" />
          <p className="text-lg font-bold">Akses Terbatas</p>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Anda tidak memiliki akses ke dashboard admin.
          </p>
        </Card>
      </div>
    );
  }

  const quickLinks = [
    {
      title: 'Manajemen Pengguna',
      description: 'Kelola akun pengguna',
      icon: <Users className="w-6 h-6" />,
      href: '/admin/users',
      color: 'from-blue-500 to-blue-600',
      stats: `${systemStats.total_users} users`,
    },
    {
      title: 'Role & Hak Akses',
      description: 'Atur role dan akses',
      icon: <Shield className="w-6 h-6" />,
      href: '/admin/roles',
      color: 'from-purple-500 to-purple-600',
      stats: `${systemStats.total_roles} roles`,
    },
    {
      title: 'Capabilities',
      description: 'Fine-grained access',
      icon: <Lock className="w-6 h-6" />,
      href: '/admin/capabilities',
      color: 'from-indigo-500 to-indigo-600',
      stats: `${systemStats.total_capabilities} caps`,
    },
    {
      title: 'Struktur Unit',
      description: 'Struktur hierarki',
      icon: <Building2 className="w-6 h-6" />,
      href: '/admin/units',
      color: 'from-teal-500 to-teal-600',
      stats: `${systemStats.total_units} units`,
    },
    {
      title: 'Data Master',
      description: 'Data referensi',
      icon: <Database className="w-6 h-6" />,
      href: '/admin/master',
      color: 'from-orange-500 to-orange-600',
      stats: 'Kategori, SPMI',
    },
    {
      title: 'Alur Kerja',
      description: 'Konfigurasi alur',
      icon: <Workflow className="w-6 h-6" />,
      href: '/admin/workflow',
      color: 'from-cyan-500 to-cyan-600',
      stats: 'Approval flows',
    },
    {
      title: 'Pengaturan Sistem',
      description: 'Konfigurasi sistem',
      icon: <Settings className="w-6 h-6" />,
      href: '/admin/settings',
      color: 'from-gray-600 to-gray-700',
      stats: 'Config',
    },
    {
      title: 'Log Audit',
      description: 'Riwayat aktivitas',
      icon: <Activity className="w-6 h-6" />,
      href: '/admin/logs',
      color: 'from-rose-500 to-rose-600',
      stats: 'Audit trail',
    },
  ];

  const logEventColors: Record<string, string> = {
    created: 'bg-success/10 text-success border-success/20',
    updated: 'bg-info/10 text-info border-info/20',
    deleted: 'bg-danger/10 text-danger border-danger/20',
    login: 'bg-teal-100 text-teal-700 border-teal-200',
    logout: 'bg-gray-100 text-gray-600 border-gray-200',
    default: 'bg-neutral-bg text-neutral-text-secondary border-neutral-border',
  };

  const getLogColor = (logName: string) => {
    return logEventColors[logName] || logEventColors.default;
  };

  const healthStatus = systemHealth.status;
  const healthStatusLabel = healthStatus === 'healthy'
    ? 'Sehat'
    : healthStatus === 'warning'
      ? 'Waspada'
      : 'Kritis';
  const healthTitleClass = healthStatus === 'healthy'
    ? 'text-green-700'
    : healthStatus === 'warning'
      ? 'text-amber-700'
      : 'text-red-700';
  const healthValueClass = healthStatus === 'healthy'
    ? 'text-green-600'
    : healthStatus === 'warning'
      ? 'text-amber-600'
      : 'text-red-600';
  const healthContainerClass = healthStatus === 'healthy'
    ? 'from-green-50 to-green-100/50 border-green-200'
    : healthStatus === 'warning'
      ? 'from-amber-50 to-amber-100/50 border-amber-200'
      : 'from-red-50 to-red-100/50 border-red-200';
  const healthIconClass = healthStatus === 'healthy'
    ? 'bg-green-500'
    : healthStatus === 'warning'
      ? 'bg-amber-500'
      : 'bg-red-500';
  const healthAlertClass = healthStatus === 'critical'
    ? 'bg-red-50 border-red-200'
    : 'bg-amber-50 border-amber-200';
  const healthAlertTextClass = healthStatus === 'critical'
    ? 'text-red-800'
    : 'text-amber-800';
  const healthAlertBodyClass = healthStatus === 'critical'
    ? 'text-red-700'
    : 'text-amber-700';
  const topHealthIssues = systemHealth.issues.slice(0, 2);

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Admin' }]} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight flex items-center gap-2">
            <Zap className="w-6 h-6 text-teal-main" />
            Admin Dashboard
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Monitor sistem dan kelola konfigurasi
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-neutral-border text-sm font-semibold rounded-lg hover:bg-neutral-bg transition-all disabled:opacity-50"
        >
          {refreshing ? <LogoLoadingMark size="xs" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {/* System Health Alert */}
      {healthStatus !== 'healthy' && (
        <div className={`flex items-start gap-3 p-4 rounded-xl border-2 ${healthAlertClass}`}>
          <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${healthStatus === 'critical' ? 'text-red-600' : 'text-amber-600'
            }`} />
          <div>
            <p className={`font-bold text-sm ${healthAlertTextClass}`}>
              System {healthStatus === 'critical' ? 'Critical' : 'Warning'}
            </p>
            <p className={`text-xs mt-1 ${healthAlertBodyClass}`}>{systemHealth.summary}</p>
            {topHealthIssues.map((issue) => (
              <p key={issue.key} className={`text-xs mt-1 ${healthAlertBodyClass}`}>
                <span className="font-semibold">{issue.label}:</span> {issue.message}
                {issue.meta ? ` (${issue.meta})` : ''}
              </p>
            ))}
          </div>
        </div>
      )}

      {stuckCount > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl border-2 bg-amber-50 border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-sm text-amber-800">
              Validasi Terhambat
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Terdapat <strong>{stuckCount} laporan</strong> yang menunggu validasi
              lebih dari 3 hari. Anda dapat melakukan override validasi dari halaman
              detail laporan jika diperlukan.
            </p>
          </div>
          <Link to="/laporan?status=diajukan">
            <button className="whitespace-nowrap px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
              Lihat Laporan
            </button>
          </Link>
        </div>
      )}

      {/* System Stats - Redesigned Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Users Card */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Total Users</p>
              <p className="text-3xl font-black text-blue-600 mt-1">{systemStats.total_users}</p>
              <p className="text-xs text-blue-600 mt-1">
                <span className="font-semibold">{systemStats.active_users}</span> aktif
              </p>
            </div>
            <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Users className="w-7 h-7 text-white" />
            </div>
          </div>
        </Card>

        {/* Laporan Card */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider">Laporan</p>
              <p className="text-3xl font-black text-purple-600 mt-1">{systemStats.total_laporan}</p>
              <p className="text-xs text-purple-600 mt-1">Total laporan</p>
            </div>
            <div className="w-14 h-14 bg-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
              <FileText className="w-7 h-7 text-white" />
            </div>
          </div>
        </Card>

        {/* Units Card */}
        <Card className="bg-gradient-to-br from-teal-50 to-teal-100/50 border-teal-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Unit Organisasi</p>
              <p className="text-3xl font-black text-teal-600 mt-1">{systemStats.total_units}</p>
              <p className="text-xs text-teal-600 mt-1">
                <span className="font-semibold">{systemStats.total_roles}</span> roles
              </p>
            </div>
            <div className="w-14 h-14 bg-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Building2 className="w-7 h-7 text-white" />
            </div>
          </div>
        </Card>

        {/* System Health Card */}
        <Card className={`bg-gradient-to-br border-2 ${healthContainerClass}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-semibold uppercase tracking-wider ${healthTitleClass}`}>
                System Health
              </p>
              <p className={`text-lg font-black mt-1 ${healthValueClass}`}>
                {healthStatus === 'healthy' ? '✓' : '⚠'}
              </p>
              <p className={`text-xs mt-1 ${healthValueClass}`}>
                {healthStatusLabel} | {systemStats.health_summary}
              </p>
              <p className={`text-xs mt-1 ${healthValueClass}`}>
                {systemStats.uptime_available ? `Uptime ${systemStats.uptime}` : systemStats.uptime_note}
              </p>
            </div>
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${healthIconClass}`}>
              {healthStatus === 'healthy' ? (
                <CheckCircle className="w-7 h-7 text-white" />
              ) : (
                <AlertTriangle className="w-7 h-7 text-white" />
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* System Info Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Database', value: systemStats.db_size, icon: <Database className="w-4 h-4" /> },
          { label: 'Storage', value: `${systemStats.storage_used_label} / ${systemStats.storage_total} GB`, icon: <HardDrive className="w-4 h-4" /> },
          { label: 'Response', value: systemStats.avg_response, icon: <Zap className="w-4 h-4" /> },
          { label: 'Version', value: `v${systemStats.app_version}`, icon: <Package className="w-4 h-4" /> },
        ].map((item, idx) => (
          <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-neutral-border">
            <div className="p-2 bg-neutral-bg rounded-lg text-neutral-text-secondary">
              {item.icon}
            </div>
            <div>
              <p className="text-[10px] text-neutral-text-secondary uppercase font-semibold">{item.label}</p>
              <p className="text-sm font-bold text-neutral-text-primary">{item.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Links - Modern Cards */}
      <div>
        <h2 className="text-lg font-bold text-teal-dark mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5" />
          Menu Administrasi
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickLinks.map((link) => (
            <Link key={link.href} to={link.href}>
              <Card hover className="h-full transition-all hover:shadow-lg hover:-translate-y-1 group overflow-hidden relative">
                {/* Gradient Background */}
                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${link.color} rounded-full blur-2xl opacity-20 group-hover:opacity-30 transition-opacity`} />

                <div className="relative">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${link.color} rounded-xl flex items-center justify-center text-white shadow-lg shrink-0`}>
                      {link.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold text-neutral-text-primary group-hover:text-teal-main transition-colors">
                          {link.title}
                        </h3>
                        <ArrowUpRight className="w-4 h-4 text-neutral-text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-xs text-neutral-text-secondary mt-0.5">
                        {link.description}
                      </p>
                      <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 bg-neutral-bg rounded-full text-neutral-text-secondary">
                        {link.stats}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <Activity className="w-5 h-5 text-rose-500" />
              Aktivitas Terkini
            </h3>
            <Link to="/admin/logs" className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1">
              Lihat Semua <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>

          {recentLogs.length > 0 ? (
            <div className="space-y-2">
              {recentLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-neutral-bg transition-colors"
                >
                  <span className="text-xs font-mono text-neutral-text-secondary w-14 shrink-0">
                    {new Date(log.created_at).toLocaleTimeString('id-ID', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 border ${getLogColor(log.log_name || 'default')}`}>
                    {(log.log_name || 'system').toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">
                      <span className="font-semibold">{log.causer?.name || 'System'}</span>
                      {' '}
                      <span className="text-neutral-text-secondary">— {log.description}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Activity className="w-10 h-10 mx-auto mb-3 text-neutral-text-secondary opacity-30" />
              <p className="text-sm text-neutral-text-secondary">Belum ada aktivitas</p>
            </div>
          )}
        </Card>

        {/* Role Distribution */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-500" />
              Distribusi Role
            </h3>
          </div>

          <div className="space-y-3">
            {roleStats.map((stat, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-neutral-text-primary truncate">
                    {stat.display_name || stat.role}
                  </span>
                  <span className="text-xs font-bold text-teal-main">{stat.count}</span>
                </div>
                <div className="w-full bg-neutral-bg rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-teal-400 to-teal-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.max(stat.percentage, 5)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-neutral-border">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-neutral-text-secondary">Total Users</span>
              <span className="text-sm font-black text-teal-main">{systemStats.total_users}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardAdmin;
